import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import BankAccountDetailsChatComposer from './BankAccountDetailsChatComposer';
import BankAccountDetailsChatThread from './BankAccountDetailsChatThread';
import { useBankAccountDetailsFlow } from './useBankAccountDetailsFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface BankAccountDetailsChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  onBack: () => void;
  onOpenPdfPreview?: () => void;
  onConsentDeclinedContinue?: () => void | Promise<void>;
}

const BankAccountDetailsChatFlow: React.FC<BankAccountDetailsChatFlowProps> = ({
  documentTitle,
  currentEmail,
  onBack,
  onOpenPdfPreview,
  onConsentDeclinedContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useBankAccountDetailsFlow(currentEmail, t);

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const greeting = useMemo(
    () => interpolateI18n(t('submitDocuments.bankAccountDetails.greetingPrefix'), { documentTitle }),
    [t, documentTitle]
  );

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.bankAccountDetails.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const atReview = flow.isComplete;
  const { showIntro: showPreviewIntroBubble, showCta: showPreviewPdfCta } = useStaggeredPreviewCta(atReview);

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [documentTitle, flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.isComplete,
    flow.consentDeclined,
    flow.editingCommitIndex,
    showPreviewIntroBubble,
  ]);

  const canShowThread = revealPhase >= 2 && !flow.isLoading;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && <AssistantBotRow animateIn>{greeting}</AssistantBotRow>}
            {flow.isLoading ? (
              <AssistantBotRow animateIn>{t('submitDocuments.bankAccountDetails.loading')}</AssistantBotRow>
            ) : canShowThread ? (
              <BankAccountDetailsChatThread flow={flow} />
            ) : null}
            {flow.consentDeclined && canShowThread && (
              <AssistantBotRow animateIn>{t('submitDocuments.bankAccountDetails.consentDeclinedMessage')}</AssistantBotRow>
            )}
            {atReview && showPreviewIntroBubble && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.consentDeclined && onConsentDeclinedContinue && (
              <ContinueButtonBar onContinue={() => void onConsentDeclinedContinue()} />
            )}
            {atReview && showPreviewPdfCta && onOpenPdfPreview && !flow.consentDeclined && (
              <PdfPreviewActionButton
                label={t('submitDocuments.bankAccountDetails.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            )}
            {!flow.isComplete && !flow.consentDeclined && !flow.isLoading ? (
              <BankAccountDetailsChatComposer flow={flow} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default BankAccountDetailsChatFlow;
